import React, { useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Printer, X } from "lucide-react";

/**
 * Printable transfer note — a clean, structured document replacing the
 * manual equipment-transfer list. Browser print uses the @media print
 * stylesheet to strip chrome and render only the note body.
 */
export default function TransferNoteDialog({ open, onOpenChange, transfer }) {
  const printRef = useRef(null);

  const handlePrint = () => {
    // Open a clean print window so the note prints standalone (no app chrome).
    const html = printRef.current?.innerHTML || "";
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Transfer Note ${transfer?.transfer_id || ""}</title>
<style>
  body { font-family: Inter, system-ui, -apple-system, Segoe UI, sans-serif; color:#0f172a; padding: 28px; margin:0; }
  h1 { font-size: 18px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
  .sub { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em; font-weight: 600; }
  .kv { display: grid; grid-template-columns: 140px 1fr; gap: 6px 14px; margin-top: 14px; font-size: 13px; }
  .kv .k { color:#64748b; text-transform: uppercase; font-size: 10px; letter-spacing: 0.06em; font-weight: 600; align-self: center; }
  .arrow { color:#6B7F3F; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 18px; font-size: 13px; }
  th, td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: left; }
  th { background: #f8fafc; font-weight: 600; font-size: 11px; text-transform: uppercase; color: #64748b; letter-spacing: 0.06em; }
  .qty { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }
  .sign-grid { margin-top: 36px; display: grid; grid-template-columns: 1fr 1fr; gap: 36px; font-size: 12px; color: #475569; }
  .sign-line { margin-top: 38px; border-top: 1px solid #94a3b8; padding-top: 6px; }
  .footer { margin-top: 28px; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
  .badge { display: inline-block; background:#F4F6EC; border:1px solid #D9E0BD; color:#6B7F3F; padding:2px 8px; border-radius: 999px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; }
</style></head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* noop */ } }, 250);
  };

  if (!transfer) return null;
  const lineCount = transfer.line_items?.length || 0;
  const totalQty = (transfer.line_items || []).reduce((acc, l) => acc + (l.quantity || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="transfer-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>Transfer Note</span>
            <button
              data-testid="transfer-note-print"
              onClick={handlePrint}
              className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 bg-olive text-white hover:opacity-90"
            >
              <Printer size={13} strokeWidth={2} /> Print
            </button>
          </DialogTitle>
        </DialogHeader>

        <div
          ref={printRef}
          className="border border-slate-200 rounded-2xl p-5 lg:p-6 bg-white"
          data-testid="transfer-note-body"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="sub" style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
                VOW Control Center · Inventory
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: "4px 0 0", letterSpacing: "-0.01em" }}>
                Equipment / Stock Transfer Note
              </h1>
            </div>
            <span className="badge" style={{ display: "inline-block", background: "#F4F6EC", border: "1px solid #D9E0BD", color: "#6B7F3F", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {transfer.status || "Completed"}
            </span>
          </div>

          <div className="kv" style={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 6, columnGap: 14, marginTop: 14, fontSize: 13 }}>
            <div className="k" style={kStyle}>Transfer ID</div>
            <div data-testid="note-tid">{transfer.transfer_id}</div>
            <div className="k" style={kStyle}>Date</div>
            <div>{new Date(transfer.date).toLocaleString()}</div>
            <div className="k" style={kStyle}>From</div>
            <div>{transfer.source_site}</div>
            <div className="k" style={kStyle}>To</div>
            <div>{transfer.destination_site}</div>
            <div className="k" style={kStyle}>Initiated by</div>
            <div>{transfer.initiated_by_name || "—"}</div>
            {transfer.notes && (
              <>
                <div className="k" style={kStyle}>Notes</div>
                <div>{transfer.notes}</div>
              </>
            )}
          </div>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Unit</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Quantity</th>
                <th style={thStyle}>Condition</th>
              </tr>
            </thead>
            <tbody>
              {(transfer.line_items || []).map((l, i) => (
                <tr key={i} data-testid={`note-line-${i}`}>
                  <td style={tdStyle}>{i + 1}</td>
                  <td style={tdStyle}>{l.item_name || l.item_id}</td>
                  <td style={tdStyle}>{l.unit || "—"}</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{l.quantity}</td>
                  <td style={tdStyle}>{l.condition || "—"}</td>
                </tr>
              ))}
              <tr>
                <td style={tdStyle}></td>
                <td style={tdStyle} colSpan={2}><strong style={{ color: "#475569" }}>{lineCount} line{lineCount === 1 ? "" : "s"}</strong></td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>{totalQty}</td>
                <td style={tdStyle}>total units</td>
              </tr>
            </tbody>
          </table>

          <div className="sign-grid" style={{ marginTop: 36, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 36, fontSize: 12, color: "#475569" }}>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.06em" }}>Released by</div>
              <div className="sign-line" style={{ marginTop: 38, borderTop: "1px solid #94a3b8", paddingTop: 6 }}>Name &amp; signature · {transfer.source_site}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.06em" }}>Received by</div>
              <div className="sign-line" style={{ marginTop: 38, borderTop: "1px solid #94a3b8", paddingTop: 6 }}>Name &amp; signature · {transfer.destination_site}</div>
            </div>
          </div>

          <div className="footer" style={{ marginTop: 28, fontSize: 10, color: "#94a3b8", borderTop: "1px solid #e2e8f0", paddingTop: 10 }}>
            Generated by VOW Control Center · {new Date().toLocaleString()} · Transfer ID {transfer.transfer_id} · This note is reprintable from the inventory transfers history.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button
            data-testid="transfer-note-close"
            onClick={() => onOpenChange(false)}
            className="rounded-full px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
          >
            <X size={13} strokeWidth={2} /> Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const kStyle = { color: "#64748b", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em", fontWeight: 600, alignSelf: "center" };
const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 18, fontSize: 13 };
const thStyle = { padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "left", background: "#f8fafc", fontWeight: 600, fontSize: 11, textTransform: "uppercase", color: "#64748b", letterSpacing: "0.06em" };
const tdStyle = { padding: "10px 12px", borderBottom: "1px solid #e2e8f0", textAlign: "left" };
